import apiClient from './apiClient';

/**
 * Cache für Zähler-Daten und Zählerstände mit Time-to-Live.
 * Reduziert redundante API-Aufrufe bei Navigation und Tab-Wechsel.
 *
 * TTLs:
 * - Zähler-Listen + Details: 15 Minuten (analog propertyCacheService)
 * - Zählerstände: 5 Minuten (häufiger mutiert, analog utilityBillCacheService)
 *
 * Pattern analog zu propertyCacheService.js:
 * - In-Memory Map + localStorage-Persistenz (Versionspräfix zaehlers:v2:)
 * - cacheInvalidationToken-Race-Condition-Schutz
 * - pendingRequests-Map gegen Cache-Stampede (Dedup)
 * - wrapSharedPromiseForCaller für AbortController-Support
 * - ETag / If-None-Match (304 Not Modified)
 *
 * Versionshistorie:
 * - v2: ZaehlerDTO um parentZaehlerRef/subZaehlerRefs erweitert (Gesamt-/Einzelzähler).
 *   Alte v1-Einträge enthalten diese Felder nicht und würden bis zu 15 Minuten
 *   unvollständige DTOs liefern — daher Versionsbump statt In-Place-Migration.
 */
const CACHE_VERSION = 'v2';
const CACHE_PREFIX = `zaehlers:${CACHE_VERSION}:`;
const LEGACY_CACHE_PREFIXES = ['zaehlers:v1:'];

// Unterschiedliche TTLs je nach Datenart
const CACHE_TTL_LIST_MS = 15 * 60 * 1000;  // 15 Minuten für Zähler-Listen/-Details
const CACHE_TTL_STAENDE_MS = 5 * 60 * 1000; // 5 Minuten für Zählerstände

const memoryCache = new Map();
const pendingRequests = new Map();

let cacheInvalidationToken = 0;

const isLocalStorageAvailable = () => {
  try {
    const testKey = '__zaehler_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

/**
 * Räumt beim Laden des Moduls verwaiste localStorage-Einträge einer älteren
 * Cache-Version auf (Best-Effort). Kein etabliertes Muster in den übrigen
 * *CacheService.js-Dateien vorhanden — hier lokal ergänzt, da das DTO-Schema
 * sich geändert hat (siehe Versionshistorie oben).
 */
const cleanupLegacyCacheVersions = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && LEGACY_CACHE_PREFIXES.some(prefix => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren
  }
};
cleanupLegacyCacheVersions();

/**
 * Prüft ob ein Cache-Eintrag noch frisch ist.
 * @param {object} entry - Cache-Eintrag { data, timestamp, etag, ttl }
 * @returns {boolean}
 */
const isFresh = (entry) => {
  if (!entry) return false;
  const ttl = entry.ttl || CACHE_TTL_LIST_MS;
  return Date.now() - entry.timestamp < ttl;
};

const getFromLocalStorage = (key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(CACHE_PREFIX + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

const setToLocalStorage = (key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded — Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

const invalidateCacheKey = (key) => {
  memoryCache.delete(key);
  pendingRequests.delete(key);
  removeFromLocalStorage(key);
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) {
    return sharedPromise;
  }

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

const clearLocalStorageCache = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

/**
 * Generische Cache-Funktion für GET-Requests.
 * @param {string} url - API-Pfad
 * @param {object} [params] - Query-Parameter
 * @param {AbortSignal} [signal] - AbortController-Signal
 * @param {number} [ttl] - TTL in ms (Standard: CACHE_TTL_LIST_MS)
 * @param {number} [_retryCount=0] - Interner Retry-Zähler
 * @returns {Promise<Array|object>}
 */
const getCached = (url, params, signal, ttl = CACHE_TTL_LIST_MS, _retryCount = 0) => {
  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  // 1. Prüfe Memory-Cache (schnellster Pfad)
  const memoryCached = memoryCache.get(cacheKey);
  if (isFresh(memoryCached)) {
    return Promise.resolve(Array.isArray(memoryCached.data) ? [...memoryCached.data] : memoryCached.data);
  }
  if (memoryCached) {
    memoryCache.delete(cacheKey);
  }

  // 2. Prüfe localStorage-Cache
  const localCached = getFromLocalStorage(cacheKey);
  if (isFresh(localCached)) {
    memoryCache.set(cacheKey, localCached);
    return Promise.resolve(Array.isArray(localCached.data) ? [...localCached.data] : localCached.data);
  }
  if (localCached) {
    removeFromLocalStorage(cacheKey);
  }

  // 3. Deduplizierung: laufenden Request wiederverwenden
  if (pendingRequests.has(cacheKey)) {
    // Kein separater signal?.aborted-Guard nötig: wrapSharedPromiseForCaller prüft
    // signal.aborted selbst und rejected sofort, falls das Signal bereits gesetzt ist.
    return wrapSharedPromiseForCaller(
      pendingRequests.get(cacheKey).then(
        data => Array.isArray(data) ? [...data] : data
      ),
      signal
    );
  }

  // 4. API-Request mit ETag-Support
  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;
  // Signal wird NICHT an axios weitergegeben: Ein StrictMode-Cleanup-Abort darf den
  // gemeinsamen HTTP-Request nicht abbrechen, während ein zweiter Caller bereits am
  // geteilten pendingRequests-Promise hängt. Die per-Caller-Isolation übernimmt
  // wrapSharedPromiseForCaller; der HTTP-Request läuft immer bis zum Abschluss durch.

  const staleEntry = localCached || memoryCached;
  const etag = staleEntry?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  const currentToken = cacheInvalidationToken;

  const promise = apiClient.get(url, config).then(response => {
    // RACE-SCHUTZ: Nicht cachen wenn invalidate aufgerufen wurde
    if (currentToken !== cacheInvalidationToken) {
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      if (response.status === 304) {
        const cachedData = staleEntry?.data;
        if (cachedData) return cachedData;
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, ttl, _retryCount + 1);
        }
        return Array.isArray(response.data) ? [...response.data] : (response.data || []);
      }
      return Array.isArray(response.data) ? [...response.data] : response.data;
    }

    if (response.status === 304) {
      if (!staleEntry) {
        if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
        invalidateCacheKey(cacheKey);
        if (_retryCount < 1 && !signal?.aborted) {
          return getCached(url, params, signal, ttl, _retryCount + 1);
        }
        return [];
      }
      const refreshedEntry = {
        data: staleEntry.data,
        timestamp: Date.now(),
        etag: staleEntry.etag,
        ttl
      };
      memoryCache.set(cacheKey, refreshedEntry);
      setToLocalStorage(cacheKey, refreshedEntry);
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      return staleEntry.data;
    }

    // 200 OK: Neue Daten cachen
    const data = response.data;
    const responseEtag = response.headers['etag'];
    const newEntry = {
      data,
      timestamp: Date.now(),
      etag: responseEtag,
      ttl
    };
    memoryCache.set(cacheKey, newEntry);
    setToLocalStorage(cacheKey, newEntry);
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    return data;
  }).catch(err => {
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    throw err;
  });

  pendingRequests.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(
    promise.then(data => Array.isArray(data) ? [...data] : data),
    signal
  );
};

/**
 * Löscht alle Cache-Einträge (Memory + Pending-Requests + localStorage), deren Key
 * das Prädikat erfüllt.
 *
 * WICHTIG (F4-Fix): Es wird sowohl über `memoryCache.keys()` ALS AUCH über
 * `pendingRequests.keys()` iteriert. Ein erstmalig laufender GET (Key nur in
 * pendingRequests, noch nicht in memoryCache) würde sonst die Invalidierung
 * überleben: Ein nachfolgender Caller würde per Dedup (Schritt 3 in getCached)
 * die ALTE, bereits-in-Flight-Promise wiederverwenden und stale Daten erhalten,
 * statt einen frischen Request auszulösen.
 * @param {(key: string) => boolean} predicate
 */
const invalidateMatchingKeys = (predicate) => {
  const candidateKeys = new Set([...memoryCache.keys(), ...pendingRequests.keys()]);
  for (const key of candidateKeys) {
    if (predicate(key)) {
      memoryCache.delete(key);
      pendingRequests.delete(key);
      removeFromLocalStorage(key);
    }
  }
  if (storageAvailable) {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(CACHE_PREFIX)) {
          const cacheKey = storageKey.substring(CACHE_PREFIX.length);
          if (predicate(cacheKey)) {
            localStorage.removeItem(storageKey);
          }
        }
      }
    } catch {
      // Ignorieren
    }
  }
};

/**
 * Invalidiert alle Listen-Cache-Einträge (verschiedene Filter-Kombinationen).
 */
const invalidateListEntries = () => {
  invalidateMatchingKeys(key => key === '/zaehlers' || key.startsWith('/zaehlers?'));
};

/**
 * Invalidiert alle Zählerstände-Cache-Einträge für einen bestimmten Zähler.
 * @param {string} zaehlerId - UUID des Zählers
 */
const invalidateStaendeEntries = (zaehlerId) => {
  const staendeKey = `/zaehlers/${zaehlerId}/staende`;
  invalidateCacheKey(staendeKey);
};

const ZAEHLER_DETAIL_KEY_PATTERN = /^\/zaehlers\/[0-9a-fA-F-]+$/;
const ZAEHLER_DIFFERENZ_KEY_PATTERN = /^\/zaehlers\/[0-9a-fA-F-]+\/differenz$/;

/**
 * Invalidiert alle Zähler-Detail- UND Differenz-Cache-Einträge (Keys `/zaehlers/<uuid>`
 * bzw. `/zaehlers/<uuid>/differenz`), aber ausdrücklich NICHT die `/staende`-Einträge.
 * Nötig, weil der Kinder-Sync im Backend (Zuordnung von Gesamt-/Einzelzählern) auch
 * FREMDE Zähler-DTOs (parentZaehlerRef/subZaehlerRefs) verändert, deren IDs hier
 * nicht bekannt sind.
 */
const invalidateAllZaehlerDetailEntries = () => {
  invalidateMatchingKeys(key =>
    ZAEHLER_DETAIL_KEY_PATTERN.test(key) || ZAEHLER_DIFFERENZ_KEY_PATTERN.test(key)
  );
};

/**
 * Invalidiert alle Differenz-Cache-Einträge (`/zaehlers/<uuid>/differenz`).
 * Der Zählerstand eines Einzelzählers ändert die Differenzberechnung seines
 * Gesamtzählers, dessen ID an dieser Stelle unbekannt ist — daher werden
 * pauschal alle Differenz-Einträge invalidiert.
 */
const invalidateAllZaehlerDifferenzEntries = () => {
  invalidateMatchingKeys(key => ZAEHLER_DIFFERENZ_KEY_PATTERN.test(key));
};

const zaehlerCacheService = {
  /**
   * Lädt alle Zähler (gecacht, 15min TTL), optional gefiltert.
   * @param {object} [options]
   * @param {string} [options.propertyId] - UUID der Immobilie
   * @param {string} [options.artCode] - Art-Code (z.B. 'GAS')
   * @param {AbortSignal} [options.signal] - AbortController-Signal
   * @returns {Promise<Array>} Liste der ZaehlerDTOs
   */
  getZaehlers: ({ propertyId, artCode, signal } = {}) => {
    const params = {};
    if (propertyId) params.propertyId = propertyId;
    if (artCode) params.artCode = artCode;
    return getCached('/zaehlers', Object.keys(params).length > 0 ? params : undefined, signal, CACHE_TTL_LIST_MS);
  },

  /**
   * Lädt einen einzelnen Zähler (gecacht, 15min TTL).
   * @param {string} id - UUID des Zählers
   * @param {object} [options]
   * @param {AbortSignal} [options.signal]
   * @returns {Promise<object>} ZaehlerDTO
   */
  getZaehler: (id, { signal } = {}) => getCached(`/zaehlers/${id}`, undefined, signal, CACHE_TTL_LIST_MS),

  /**
   * Erstellt einen neuen Zähler und invalidiert den Listen-Cache.
   * @param {object} payload - { artId, einheit?, ort?, notiz?, propertyIds: UUID[] }
   * @returns {Promise<object>} erstellter ZaehlerDTO
   */
  createZaehler: async (payload) => {
    const response = await apiClient.post('/zaehlers', payload);
    cacheInvalidationToken++;
    invalidateListEntries();
    // Der Kinder-Sync (parentZaehlerId/subZaehlerIds) kann fremde Zähler-DTOs verändern.
    invalidateAllZaehlerDetailEntries();
    return response.data;
  },

  /**
   * Aktualisiert einen Zähler und invalidiert Listen- und Detail-Cache.
   * @param {string} id - UUID des Zählers
   * @param {object} payload
   * @returns {Promise<object>} aktualisierter ZaehlerDTO
   */
  updateZaehler: async (id, payload) => {
    const response = await apiClient.put(`/zaehlers/${id}`, payload);
    cacheInvalidationToken++;
    invalidateListEntries();
    invalidateCacheKey(`/zaehlers/${id}`);
    // Der Kinder-Sync (parentZaehlerId/subZaehlerIds) kann fremde Zähler-DTOs verändern.
    invalidateAllZaehlerDetailEntries();
    return response.data;
  },

  /**
   * Löscht einen Zähler und invalidiert Listen- und Detail-Cache.
   * @param {string} id - UUID des Zählers
   * @returns {Promise<void>}
   */
  deleteZaehler: async (id) => {
    await apiClient.delete(`/zaehlers/${id}`);
    cacheInvalidationToken++;
    invalidateListEntries();
    invalidateCacheKey(`/zaehlers/${id}`);
    // Löschen eines Gesamt-/Einzelzählers hebt die Zuordnung bei fremden Zähler-DTOs auf.
    invalidateAllZaehlerDetailEntries();
  },

  /**
   * Lädt alle Zählerstände für einen Zähler (gecacht, 5min TTL).
   * Liste ist absteigend sortiert (neueste zuerst) und enthält
   * berechnete Felder verbrauch und jahresverbrauch.
   * @param {string} zaehlerId - UUID des Zählers
   * @param {object} [options]
   * @param {AbortSignal} [options.signal]
   * @returns {Promise<Array>} Liste der ZaehlerstandDTOs
   */
  getZaehlerstaende: (zaehlerId, { signal } = {}) =>
    getCached(`/zaehlers/${zaehlerId}/staende`, undefined, signal, CACHE_TTL_STAENDE_MS),

  /**
   * Lädt die Differenzberechnung (Allgemeinverbrauch) eines Gesamtzählers gegenüber
   * der Summe seiner Einzelzähler (gecacht, 5min TTL wie die Zählerstände).
   * @param {string} zaehlerId - UUID des Gesamtzählers
   * @param {object} [options]
   * @param {AbortSignal} [options.signal]
   * @returns {Promise<Array>} Liste { jahr, verbrauchGesamt, summeEinzelzaehler, differenz,
   *   vollstaendig, einzelwerte } absteigend nach Jahr sortiert
   */
  getZaehlerDifferenz: (zaehlerId, { signal } = {}) =>
    getCached(`/zaehlers/${zaehlerId}/differenz`, undefined, signal, CACHE_TTL_STAENDE_MS),

  /**
   * Erstellt einen neuen Zählerstand.
   * Invalidiert: Zählerstände, Zähler-Detail (wegen letzterStand), Listen, Differenz
   * (der Stand kann die Differenzberechnung eines fremden Gesamtzählers ändern).
   * @param {string} zaehlerId - UUID des Zählers
   * @param {object} payload - { ablesedatum, wert, zaehlerwechsel?, notiz? }
   * @returns {Promise<object>} erstellter ZaehlerstandDTO
   */
  createZaehlerstand: async (zaehlerId, payload) => {
    const response = await apiClient.post(`/zaehlers/${zaehlerId}/staende`, payload);
    cacheInvalidationToken++;
    invalidateStaendeEntries(zaehlerId);
    invalidateCacheKey(`/zaehlers/${zaehlerId}`);
    invalidateListEntries();
    invalidateAllZaehlerDifferenzEntries();
    return response.data;
  },

  /**
   * Aktualisiert einen Zählerstand.
   * @param {string} zaehlerId - UUID des Zählers
   * @param {string} standId - UUID des Stands
   * @param {object} payload
   * @returns {Promise<object>} aktualisierter ZaehlerstandDTO
   */
  updateZaehlerstand: async (zaehlerId, standId, payload) => {
    const response = await apiClient.put(`/zaehlers/${zaehlerId}/staende/${standId}`, payload);
    cacheInvalidationToken++;
    invalidateStaendeEntries(zaehlerId);
    invalidateCacheKey(`/zaehlers/${zaehlerId}`);
    invalidateListEntries();
    invalidateAllZaehlerDifferenzEntries();
    return response.data;
  },

  /**
   * Löscht einen Zählerstand.
   * @param {string} zaehlerId - UUID des Zählers
   * @param {string} standId - UUID des Stands
   * @returns {Promise<void>}
   */
  deleteZaehlerstand: async (zaehlerId, standId) => {
    await apiClient.delete(`/zaehlers/${zaehlerId}/staende/${standId}`);
    cacheInvalidationToken++;
    invalidateStaendeEntries(zaehlerId);
    invalidateCacheKey(`/zaehlers/${zaehlerId}`);
    invalidateListEntries();
    invalidateAllZaehlerDifferenzEntries();
  },

  /**
   * Invalidiert alle Zähler-Caches (Listen + Details + Stände).
   * Wird z.B. bei Tenant-/Login-Wechsel aufgerufen.
   */
  invalidateAll: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Löscht den kompletten Cache (bei Logout).
   */
  clearCache: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Invalidiert den Listen-Cache (nach Create, Update, Delete).
   */
  invalidateList: () => {
    cacheInvalidationToken++;
    invalidateListEntries();
  },

  /**
   * Für Unit-Tests: gibt interne Cache-Objekte zurück.
   * @returns {{ memoryCache: Map, pendingRequests: Map }}
   */
  _getInternals: () => ({ memoryCache, pendingRequests }),

  /**
   * Für Unit-Tests: setzt den Cache komplett zurück.
   */
  _reset: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  }
};

export default zaehlerCacheService;
